import { useState } from "react"
import styled from "styled-components"

import { Banner, Button, colors } from "@minuthq/meatball-ui-react"
import { Text } from "@minuthq/meatball-ui-react"

import { UpgradeBlockerButton } from "src/components/FeatureBlockers/UpgradeBlockerButton"
import { Violations } from "src/data/homes/types/homeTypes"
import { langKeys } from "src/i18n/langKeys"
import { TTranslateFunction, useTranslate } from "src/i18n/useTranslate"
import { ConfirmDialog } from "src/ui/Dialog/ConfirmDialog"
import InfoIcon from "src/ui/icons/info.svg"
import { spacing } from "src/ui/spacing"

function violationText(vType: Violations, t: TTranslateFunction) {
  switch (vType) {
    case "multiple_homes_in_one_virtual_home":
      return (
        <div>
          <Text variant="heading3">
            {t(langKeys.violation_banner_multiple_homes_title)}
          </Text>
          <Text>{t(langKeys.violation_banner_multiple_homes_description)}</Text>
          <Text>{t(langKeys.violation_banner_multiple_homes_note)}</Text>
        </div>
      )
    case "number_of_members":
      return (
        <div>
          <Text variant="heading3">
            {t(langKeys.violation_banner_number_of_members_title)}
          </Text>
          <Text>
            {t(langKeys.violation_banner_number_of_members_description)}
          </Text>
        </div>
      )
    case "number_of_homes":
      return (
        <div>
          <Text variant="heading3">
            {t(langKeys.violation_banner_number_of_homes_title)}
          </Text>
          <Text>
            {t(langKeys.violation_banner_number_of_homes_description)}
          </Text>
        </div>
      )
    default:
      return null
  }
}

export function ViolationBanner({ violations }: { violations?: Violations[] }) {
  const { t, langKeys } = useTranslate()

  const [moreModalOpen, setMoreModalOpen] = useState(false)

  if (!violations?.length) {
    return null
  }

  return (
    <div>
      <Banner
        title={t(langKeys.update_your_plan)}
        icon={<InfoIcon width={32} color={colors.systemErrorDark} />}
        type="error"
        fullWidth
      >
        {t(langKeys.violation_banner_description)}

        <ButtonRow>
          <UpgradeBlockerButton
            eventProperties={{ context: "violation_banner" }}
            size="small"
          />
          <Button onClick={() => setMoreModalOpen(true)} variant="minimal">
            {t(langKeys.read_more)}
          </Button>
        </ButtonRow>
      </Banner>

      <ConfirmDialog
        open={moreModalOpen}
        title={t(langKeys.update_your_plan)}
        description={t(langKeys.violation_banner_description)}
        onClose={() => setMoreModalOpen(false)}
      >
        <>
          <ModalContents>
            {violations.map((vType, index) => {
              return (
                <ViolationItem key={index}>
                  <InfoIcon
                    height={24}
                    width={24}
                    color={colors.systemErrorDark}
                  />
                  {violationText(vType, t)}
                </ViolationItem>
              )
            })}
          </ModalContents>
          <Text>{t(langKeys.violation_banner_further_support)}</Text>
        </>
      </ConfirmDialog>
    </div>
  )
}

const ModalContents = styled.div`
  display: grid;
  margin: ${spacing.XL} 0;
  gap: ${spacing.M};
`

const ViolationItem = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: ${spacing.M};
`

const ButtonRow = styled.div`
  margin-top: ${spacing.M};
  display: flex;
  align-items: center;
  gap: ${spacing.M};
  flex-wrap: wrap;
`
